import { FilterQuery } from '@yes.technology/react-toolkit'
import { FilterFieldsResponse } from 'shared/hooks/useFilterFields/useFilterFields'
import { FieldSettings } from 'shared/types'
import { GroupedFieldValues } from 'site/Renderer/components/FilterFields/types'
import { RelationConfigProps } from 'site/Renderer/components/ObjectView/components/types/object-view'
import useObjectFields from 'site/Renderer/components/ObjectView/hooks/useObjectFields'
import { useSiteState } from 'siteState/shared'
import {
  useFetchYesObjects,
  usePopulateObjectRelations
} from 'yesObject/shared'
import useRelationsTableLayouts from '../useRelationsTableLayouts/useRelationsTableLayouts'

type UseReportDataProps = {
  filteractionUuid?: string
  enabled?: boolean
  relationConfig?: RelationConfigProps
  fieldSettings?: FieldSettings
  classificationUuid?: string
  reportType: 'list' | 'card'
}

export default function useReportData({
  filteractionUuid,
  enabled,
  relationConfig,
  fieldSettings,
  classificationUuid,
  reportType
}: UseReportDataProps) {
  const [fieldValues] = useSiteState<GroupedFieldValues>('field-values')
  const [filterFields] = useSiteState<FilterFieldsResponse>('filter-fields')
  const [filterQuery] = useSiteState<FilterQuery>('filter-query')
  const [filterComposition] = useSiteState<string>('field-composition')
  const [objectclassUuid] = useSiteState<string>('objectclassuuid')
  const [filteractionName] = useSiteState<string>('filteraction-name')

  const {
    yesObjects,
    isLoading: isLoadingYesObjects,
    isFetched: isFetchedYesObjects,
    error: errorYesObjects
  } = useFetchYesObjects({
    filterBody: {
      query: filterQuery,
      objectclass: objectclassUuid
    },
    filteractionUuid,
    initialPagination: {},
    enabled
  })

  const objectFields = useObjectFields({
    objectclassUuid,
    classificationUuid,
    fieldSettings,
    enabled
  })

  const {
    relationsTableLayouts,
    isLoading: isLoadingRelationsLayouts,
    isFetched: isFetchedRelationsLayouts,
    error: errorRelationsLayout
  } = useRelationsTableLayouts({
    relationConfig,
    enabled: enabled && reportType === 'card'
  })

  const {
    data,
    isFetching: isLoadingPopulateObjectRelations,
    isFetched: isFetchedPopulateObjectRelations,
    error: errorPopulateObjectRelations
  } = usePopulateObjectRelations({
    objects: yesObjects,
    relationFields: objectFields.relationFields,
    enabled: enabled && isFetchedYesObjects
  })

  return {
    yesObjects: data,
    requestLoading:
      isLoadingYesObjects ||
      isLoadingRelationsLayouts ||
      isLoadingPopulateObjectRelations ||
      objectFields.isLoading,
    requestFetched:
      isFetchedYesObjects &&
      objectFields.isFetched &&
      (isFetchedRelationsLayouts || reportType !== 'card' || !relationConfig) &&
      isFetchedPopulateObjectRelations,
    error:
      errorYesObjects ||
      errorRelationsLayout ||
      errorPopulateObjectRelations ||
      objectFields.error,
    objectFields,
    fieldValues,
    filterFields,
    filterComposition,
    filteractionName,
    relationsTableLayouts
  }
}
